import React, { useState, useEffect } from 'react';
import {
  TrendingDown, DollarSign, Target, AlertCircle,
  CheckCircle, Clock, Shield, Zap
} from 'lucide-react';
import './TRMStyles.css';

const ConsolidationAnalysis = () => {
  const [summary, setSummary] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPriority, setSelectedPriority] = useState('all');

  useEffect(() => {
    fetchConsolidationData();
  }, []);

  const fetchConsolidationData = async () => {
    try {
      setLoading(true);
      const [summaryRes, oppsRes] = await Promise.all([
        fetch('/api/trm/consolidation/summary'),
        fetch('/api/trm/consolidation/recommendations')
      ]);

      const summaryData = await summaryRes.json();
      const oppsData = await oppsRes.json();

      // Calculate quick wins (low risk + ROI < 6 months)
      const quickWins = oppsData.filter(opp =>
        opp.technical_risk === 'Low' &&
        opp.business_risk === 'Low' &&
        opp.roi_months <= 6
      ).length;

      // Add computed fields to summary
      summaryData.quick_wins = quickWins;
      summaryData.approved_count = summaryData.by_status?.Approved || 0;

      setSummary(summaryData);
      setOpportunities(oppsData);
    } catch (error) {
      console.error('Error fetching consolidation data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Averages arrive as floats. Rounding happens in the query too, but formatting
  // here as well means an unrounded value can never reach the screen if another
  // endpoint ever supplies this figure.
  const formatMonths = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '0';
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const getRiskColor = (risk) => {
    switch (risk?.toLowerCase()) {
      case 'low': return 'success';
      case 'medium': return 'warning';
      case 'high': return 'danger';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'complete': return <CheckCircle size={16} className="text-success" />;
      case 'implementing': return <Clock size={16} className="text-warning" />;
      case 'approved': return <Target size={16} className="text-primary" />;
      default: return <AlertCircle size={16} className="text-secondary" />;
    }
  };

  const filteredOpportunities = selectedPriority === 'all'
    ? opportunities
    : opportunities.filter(opp => opp.priority?.toLowerCase() === selectedPriority);

  if (loading) {
    return (
      <div className="trm-loading">
        <div className="spinner"></div>
        <p>Analyzing consolidation opportunities...</p>
      </div>
    );
  }

  return (
    <div className="trm-consolidation-dashboard">
      <div className="dashboard-header">
        <h2>🎯 Software Consolidation Analysis</h2>
        <p>Identify redundancies, reduce costs, and optimize your software portfolio</p>
      </div>

      {/* Summary Metrics */}
      <div className="metric-cards">
        <div className="metric-card success">
          <div className="metric-icon">
            <DollarSign size={32} />
          </div>
          <div className="metric-content">
            <h3>Total Savings Potential</h3>
            <div className="metric-value">{formatCurrency(summary?.total_potential_savings || 0)}</div>
            <p className="metric-subtitle">Annual cost reduction</p>
          </div>
        </div>

        <div className="metric-card primary">
          <div className="metric-icon">
            <Target size={32} />
          </div>
          <div className="metric-content">
            <h3>Opportunities</h3>
            <div className="metric-value">{summary?.total_opportunities || 0}</div>
            <p className="metric-subtitle">{summary?.approved_count || 0} approved</p>
          </div>
        </div>

        <div className="metric-card warning">
          <div className="metric-icon">
            <TrendingDown size={32} />
          </div>
          <div className="metric-content">
            <h3>Avg ROI Period</h3>
            <div className="metric-value">{formatMonths(summary?.average_roi_months)} mo</div>
            <p className="metric-subtitle">Time to payback</p>
          </div>
        </div>

        <div className="metric-card info">
          <div className="metric-icon">
            <Shield size={32} />
          </div>
          <div className="metric-content">
            <h3>Quick Wins</h3>
            <div className="metric-value">{summary?.quick_wins || 0}</div>
            <p className="metric-subtitle">Low risk, high value</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-section">
        <div className="filter-group">
          <label>Priority:</label>
          <select value={selectedPriority} onChange={(e) => setSelectedPriority(e.target.value)}>
            <option value="all">All Priorities</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Consolidation Opportunities */}
      <div className="data-section">
        <div className="section-header">
          <h3><Zap size={20} /> Consolidation Opportunities ({filteredOpportunities.length})</h3>
        </div>
        <div className="opportunities-list">
          {filteredOpportunities.map((opp, idx) => (
            <div key={idx} className="opportunity-card">
              <div className="opportunity-header">
                <div className="opportunity-title">
                  <h4>{opp.action}: {opp.primary_product_name} ↔ {opp.redundant_product_name}</h4>
                  <div className="opportunity-badges">
                    <span className={`priority-badge priority-${opp.priority?.toLowerCase()}`}>
                      {opp.priority}
                    </span>
                    <span className={`status-badge status-${opp.status?.toLowerCase().replace(' ', '-')}`}>
                      {getStatusIcon(opp.status)} {opp.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="opportunity-body">
                <p className="rationale">{opp.rationale}</p>

                <div className="opportunity-metrics">
                  <div className="metric-item">
                    <label>Overlap</label>
                    <span className="value">{opp.overlap_percentage}%</span>
                  </div>
                  <div className="metric-item success">
                    <label>Annual Savings</label>
                    <span className="value">{formatCurrency(opp.estimated_savings)}</span>
                  </div>
                  <div className="metric-item">
                    <label>Implementation Cost</label>
                    <span className="value">{formatCurrency(opp.implementation_cost)}</span>
                  </div>
                  <div className="metric-item">
                    <label>ROI</label>
                    <span className="value">{opp.roi_months} months</span>
                  </div>
                </div>

                <div className="risk-assessment">
                  <div className={`risk-item risk-${getRiskColor(opp.technical_risk)}`}>
                    <Shield size={14} />
                    <span>Technical: {opp.technical_risk}</span>
                  </div>
                  <div className={`risk-item risk-${getRiskColor(opp.business_risk)}`}>
                    <AlertCircle size={14} />
                    <span>Business: {opp.business_risk}</span>
                  </div>
                  <div className={`risk-item risk-${getRiskColor(opp.migration_effort)}`}>
                    <Zap size={14} />
                    <span>Effort: {opp.migration_effort}</span>
                  </div>
                </div>

                {opp.sme_notes && (
                  <div className="sme-notes">
                    <strong>SME Notes:</strong> {opp.sme_notes}
                    <br />
                    <small>Reviewed by {opp.sme_reviewed_by} on {new Date(opp.sme_reviewed_at).toLocaleDateString()}</small>
                  </div>
                )}
              </div>
            </div>
          ))}

          {filteredOpportunities.length === 0 && (
            <div className="no-data">
              <AlertCircle size={48} />
              <p>No consolidation opportunities found for this filter</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConsolidationAnalysis;
